<template>
  <div
    class="ui-field"
    :class="[$attrs.class, `ui-field--${size}`, { 'ui-field--error': hasError, 'ui-field--disabled': disabled, 'ui-field--floating': floatingError }]"
    :style="rootStyle"
  >
    <label v-if="label" :for="resolvedId" class="ui-field__label">
      {{ label }}
      <span v-if="required" aria-hidden="true" class="ui-field__required">*</span>
      <slot name="tooltip" />
    </label>
    <div
      class="ui-field__control"
      :class="{ 'ui-field__control--error': hasError && showErrorBorder }"
    >
      <span v-if="$slots.prefix" class="ui-field__addon ui-field__addon--prefix">
        <slot name="prefix" />
      </span>
      <span v-else-if="type === 'email' && !disableAutoPrefix" class="ui-field__floating-icon">
        <Icon name="Mail01" :size="20" />
      </span>
      <span v-if="$slots.floatingIcon" class="ui-field__floating-icon">
        <slot name="floatingIcon" />
      </span>
      <input
        :id="resolvedId"
        ref="element"
        data-test-id="ui-input"
        v-bind="nativeAttrs"
        :aria-describedby="message ? messageId : undefined"
        :aria-errormessage="hasError && message ? messageId : undefined"
        :aria-invalid="hasError || undefined"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :type="type"
        :value="modelValue"
        @input="update"
      >
      <button
        v-if="clearable && modelValue !== '' && modelValue !== undefined"
        aria-label="Wyczyść"
        data-test-id="ui-input-clear"
        class="ui-field__clear"
        :disabled="disabled"
        type="button"
        @click="clear"
      >
        <Icon name="XClose" :size="16" />
      </button>
      <span v-if="$slots.suffix" class="ui-field__addon ui-field__addon--suffix">
        <slot name="suffix" />
      </span>
    </div>
    <p v-if="message" :id="messageId" class="ui-field__message">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, useAttrs, useId } from 'vue'

  import { Icon } from '../Icon'

  defineOptions({ inheritAttrs: false, name: 'UiInput' })
  const props = withDefaults(
    defineProps<{
      clearable?: boolean
      disableAutoPrefix?: boolean
      disabled?: boolean
      error?: boolean | string
      floatingError?: boolean
      helperText?: string
      id?: string
      label?: string
      modelValue?: number | string
      name?: string
      placeholder?: string
      required?: boolean
      showErrorBorder?: boolean
      size?: 'md' | 'sm'
      type?: string
    }>(),
    { modelValue: '', showErrorBorder: true, size: 'sm', type: 'text' },
  )
  const emit = defineEmits<{ clear: []; 'update:modelValue': [value: string] }>()
  const attrs = useAttrs()
  const generatedId = useId()
  const element = ref<HTMLInputElement>()
  const resolvedId = computed(() => props.id ?? `ui-input-${generatedId}`)
  const messageId = computed(() => `${resolvedId.value}-message`)
  const hasError = computed(
    () => props.error === true || (typeof props.error === 'string' && props.error.length > 0),
  )
  const message = computed(() =>
    typeof props.error === 'string' && props.error ? props.error : props.helperText,
  )
  const nativeAttrs = computed(() =>
    Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style')),
  )
  const rootStyle = computed(() => attrs.style)
  const update = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLInputElement).value)
  }
  const clear = () => {
    emit('update:modelValue', '')
    emit('clear')
    element.value?.focus()
  }
  const focus = () => element.value?.focus()
  defineExpose({ element, focus })
</script>

<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Disabled, error and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */


.ui-field {
 display: flex;
 flex-direction: column;
 gap: 6px;
 position: relative;
 width: 100%;

}

.ui-field__label {
 @include typography.font-style('text-sm', 'medium');

 color: var(--color-text-text-secondary-700);

}

.ui-field__required {
 color: var(--color-brand-600);

}

.ui-field__control {
 align-items: center;
 background: var(--color-background-bg-primary);
 border: 1px solid var(--color-border-border-primary);
 border-radius: var(--radius-md);
 box-shadow: 0 1px 2px var(--color-effects-shadows-shadow-xs);
 display: flex;
 min-width: 0;
 transition: border-color 0.2s ease, box-shadow 0.2s ease;

}

.ui-field--md .ui-field__control {
 height: 44px;
}

.ui-field--sm .ui-field__control {
 height: 40px;
}

.ui-field__control:hover:not(.ui-field__control--error) {
 border-color: var(--color-gray-light-400);

}

.ui-field__control:focus-within {
 border-color: var(--color-border-border-brand);
 outline: 1px solid var(--color-border-border-brand);

}

.ui-field__control--error {
 border-color: var(--color-border-border-error-subtle);

}

.ui-field__control--error:focus-within {
 border-color: var(--color-border-border-error-subtle);
 outline: 1px solid var(--color-border-border-error-subtle);

}

input {
 @include typography.font-style('text-md', 'regular');

 background: transparent;
 border: 0;
 color: var(--color-text-text-primary-900);
 min-width: 0;
 outline: 0;
 padding: 10px 14px;
 height: 100%;
 width: 100%;

}

.ui-field--sm input {
 padding: 8px 12px;

}

input::placeholder {
 color: var(--color-text-text-placeholder);

}

input:disabled {
 color: var(--color-text-text-disabled);
 cursor: not-allowed;

}

.ui-field--disabled .ui-field__control {
 background: var(--color-gray-neutral-50);
 box-shadow: none;
 opacity: 0.5;

}

.ui-field__addon {
 align-items: center;
 align-self: stretch;
 color: var(--color-text-text-tertiary-600);
 display: flex;
 padding: 0 12px;

}

.ui-field__addon--prefix {
 border-right: 1px solid var(--color-border-border-primary);

}

.ui-field__addon--suffix {
 border-left: 1px solid var(--color-border-border-primary);

}

.ui-field__floating-icon {
 align-items: center;
 color: var(--color-foreground-fg-quaternary-500);
 display: flex;
 padding-left: 12px;

}

.ui-field__clear {
 align-items: center;
 background: transparent;
 border: 0;
 border-radius: var(--radius-xs);
 color: var(--color-foreground-fg-quaternary-500);
 cursor: pointer;
 display: flex;
 margin-right: 6px;
 padding: 4px;

}

.ui-field__clear:hover:not(:disabled) {
 background: var(--color-background-bg-primary-hover);
 color: var(--color-text-text-secondary-700);

}

.ui-field__clear:focus-visible {
 outline: 2px solid var(--color-effects-focus-rings-focus-ring);
 outline-offset: 1px;

}

.ui-field__message {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);
 margin: 0;

}

.ui-field--error .ui-field__message {
 color: var(--color-error-600);

}

.ui-field--floating .ui-field__message {
 background: var(--color-background-bg-primary);
 border-radius: var(--radius-xs);
 box-shadow: 0 2px 4px var(--color-effects-shadows-shadow-xs);
 left: 0;
 padding: 4px 8px;
 position: absolute;
 top: calc(100% + 4px);
 z-index: 5;

}

</style>
